// SettingsModal.jsx - Modal de configuracion con secciones colapsables
// Ubicacion: frontend/src/userPanel/components/SettingsModal.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Alert, Spinner, ProgressBar } from 'react-bootstrap';
import { getAuthHeaders } from '../../services/api/utils/fetchWrapper';
import { getCalendarAuthUrl, getCalendarStatus, disconnectCalendar } from '../../services/api';
import ResetearDatosModal from './ResetearDatosModal';

// ============= DESIGN TOKENS =============
const COLORS = {
  headerText: '#2E7D32',
  cardBorder: '#E0E0E0',
  badgeGreen: '#4CAF50',
  btnGreen: '#4CAF50',
  btnGreenHover: '#388E3C',
  btnRed: '#F44336',
  btnBlue: '#4285F4',
  tipBg: '#E8F5E9',
  tipBorder: '#A5D6A7',
  textPrimary: '#212121',
  textSecondary: '#616161',
  chevron: '#9E9E9E',
};

// ============= COLLAPSIBLE SECTION =============
function Section({ icon, title, badge, defaultOpen = false, children, visible = true }) {
  const [open, setOpen] = useState(defaultOpen);

  if (!visible) return null;

  return (
    <div style={{
      border: `1px solid ${COLORS.cardBorder}`,
      borderRadius: '12px',
      marginBottom: '12px',
      overflow: 'hidden',
      backgroundColor: '#fff',
    }}>
      {/* Header */}
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 16px',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '18px' }}>{icon}</span>
          <span style={{
            fontSize: '15px',
            fontWeight: 600,
            color: COLORS.headerText,
          }}>
            {title}
          </span>
          {badge}
        </div>
        <i
          className={`bi bi-chevron-${open ? 'up' : 'down'}`}
          style={{ color: COLORS.chevron, fontSize: '14px', transition: 'transform 0.2s ease' }}
        />
      </div>

      {/* Body with smooth animation */}
      <div style={{
        maxHeight: open ? '1000px' : '0',
        overflow: 'hidden',
        transition: 'max-height 0.3s ease',
      }}>
        <div style={{
          padding: '0 16px 16px 16px',
          borderTop: `1px solid ${COLORS.cardBorder}`,
          paddingTop: '16px',
        }}>
          {children}
        </div>
      </div>
    </div>
  );
}

// ============= MAIN COMPONENT =============
export default function SettingsModal({ show, onHide, userId, objetoId, objetoNombre, objetos }) {
  const [pinActivo, setPinActivo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Detectar si entro con magic link
  const [tieneMagicLinkBypass, setTieneMagicLinkBypass] = useState(false);

  // Estado para activar PIN
  const [nuevoPin, setNuevoPin] = useState('');
  const [nuevoPinConfirm, setNuevoPinConfirm] = useState('');

  // Estado para cambiar PIN
  const [pinActual, setPinActual] = useState('');
  const [pinNuevo, setPinNuevo] = useState('');
  const [pinNuevoConfirm, setPinNuevoConfirm] = useState('');

  // Estado para desactivar PIN
  const [pinDesactivar, setPinDesactivar] = useState('');

  // Estado para Reset Cuenta (Demo)
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resetStats, setResetStats] = useState(null);
  const [resetProgress, setResetProgress] = useState(0);
  const [tieneTagsDemo, setTieneTagsDemo] = useState(false);

  // Estado para Reemplazo de Tarjeta
  const [reemplazoConfirmText, setReemplazoConfirmText] = useState('');
  const [reemplazoExitoso, setReemplazoExitoso] = useState(false);

  // Estado para Resetear Datos
  const [showResetearModal, setShowResetearModal] = useState(false);

  // Estado para Google Calendar
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [calendarEmail, setCalendarEmail] = useState('');
  const [calendarLoading, setCalendarLoading] = useState(false);

  // Mensajes
  const [message, setMessage] = useState({ type: '', text: '' });

  // Vista activa: 'principal', 'activar', 'cambiar', 'desactivar', 'reset', 'resetCuenta', 'reemplazoTarjeta'
  const [vista, setVista] = useState('principal');

  // Cargar estado del PIN y verificar tags demo al abrir el modal
  useEffect(() => {
    if (show) {
      cargarEstadoPin();
      verificarTagsDemo();
      cargarEstadoCalendar();
      const bypass = sessionStorage.getItem('pinBypassed');
      setTieneMagicLinkBypass(bypass === 'true');
    }
  }, [show]);

  const cargarEstadoPin = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('nfcToken');
      const response = await fetch(`/api/usuarios/${userId}/pin/estado`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPinActivo(data.pinActivo || false);
      } else {
        console.error('Error obteniendo estado del PIN');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const verificarTagsDemo = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`/api/usuarios/${userId}/tiene-tags-demo`, {
        method: 'GET',
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setTieneTagsDemo(data.tieneTagsDemo || false);
      }
    } catch (error) {
      console.error('Error verificando tags demo:', error);
      setTieneTagsDemo(false);
    }
  };

  const cargarEstadoCalendar = async () => {
    try {
      const result = await getCalendarStatus();
      if (result && !result.error) {
        setCalendarConnected(result.connected || false);
        setCalendarEmail(result.email || '');
      }
    } catch (error) {
      console.error('Error cargando estado de calendario:', error);
    }
  };

  const handleConnectCalendar = async () => {
    try {
      setCalendarLoading(true);
      const result = await getCalendarAuthUrl();
      if (result.error) {
        setMessage({ type: 'danger', text: result.error });
        return;
      }
      window.location.href = result.url;
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'danger', text: 'Error de conexion' });
    } finally {
      setCalendarLoading(false);
    }
  };

  const handleDisconnectCalendar = async () => {
    try {
      setCalendarLoading(true);
      const result = await disconnectCalendar();
      if (result.error) {
        setMessage({ type: 'danger', text: result.error });
        return;
      }
      setCalendarConnected(false);
      setCalendarEmail('');
      setMessage({ type: 'success', text: 'Google Calendar desconectado' });
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'danger', text: 'Error de conexion' });
    } finally {
      setCalendarLoading(false);
    }
  };

  const cambiarVista = useCallback((nuevaVista) => {
    setVista(nuevaVista);
    setMessage({ type: '', text: '' });
    setNuevoPin('');
    setNuevoPinConfirm('');
    setPinActual('');
    setPinNuevo('');
    setPinNuevoConfirm('');
    setPinDesactivar('');
    setResetConfirmText('');
    setResetStats(null);
    setResetProgress(0);
    setReemplazoConfirmText('');
    setReemplazoExitoso(false);
  }, []);

  const validarPin = (pin) => /^[A-Za-z0-9]{3}$/.test(pin);

  // ============= HANDLERS (unchanged logic) =============

  const handleActivarPin = async (e) => {
    e.preventDefault();
    if (!validarPin(nuevoPin)) {
      setMessage({ type: 'danger', text: 'El PIN debe tener exactamente 3 caracteres alfanumericos' });
      return;
    }
    if (nuevoPin !== nuevoPinConfirm) {
      setMessage({ type: 'danger', text: 'Los PINs no coinciden' });
      return;
    }
    try {
      setSubmitting(true);
      setMessage({ type: '', text: '' });
      const token = localStorage.getItem('nfcToken');
      const response = await fetch(`/api/usuarios/${userId}/pin/activar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: nuevoPin, pinConfirm: nuevoPinConfirm })
      });
      const data = await response.json();
      if (response.ok) {
        setMessage({ type: 'success', text: 'PIN activado correctamente' });
        setPinActivo(true);
        setTimeout(() => cambiarVista('principal'), 1500);
      } else {
        setMessage({ type: 'danger', text: data.message || 'Error al activar el PIN' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'danger', text: 'Error de conexion' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCambiarPin = async (e) => {
    e.preventDefault();
    if (!validarPin(pinActual)) { setMessage({ type: 'danger', text: 'PIN actual invalido' }); return; }
    if (!validarPin(pinNuevo)) { setMessage({ type: 'danger', text: 'El nuevo PIN debe tener exactamente 3 caracteres alfanumericos' }); return; }
    if (pinNuevo !== pinNuevoConfirm) { setMessage({ type: 'danger', text: 'Los nuevos PINs no coinciden' }); return; }
    if (pinActual === pinNuevo) { setMessage({ type: 'danger', text: 'El nuevo PIN debe ser diferente al actual' }); return; }
    try {
      setSubmitting(true);
      setMessage({ type: '', text: '' });
      const token = localStorage.getItem('nfcToken');
      const response = await fetch(`/api/usuarios/${userId}/pin/cambiar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ pinActual, pinNuevo })
      });
      const data = await response.json();
      if (response.ok) {
        setMessage({ type: 'success', text: 'PIN cambiado correctamente' });
        setTimeout(() => cambiarVista('principal'), 1500);
      } else {
        setMessage({ type: 'danger', text: data.message || 'Error al cambiar el PIN' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'danger', text: 'Error de conexion' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPin = async (e) => {
    e.preventDefault();
    if (!validarPin(pinNuevo)) { setMessage({ type: 'danger', text: 'El nuevo PIN debe tener exactamente 3 caracteres alfanumericos' }); return; }
    if (pinNuevo !== pinNuevoConfirm) { setMessage({ type: 'danger', text: 'Los PINs no coinciden' }); return; }
    try {
      setSubmitting(true);
      setMessage({ type: '', text: '' });
      const token = localStorage.getItem('nfcToken');
      const response = await fetch(`/api/usuarios/${userId}/pin/reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ pinNuevo, pinNuevoConfirm })
      });
      const data = await response.json();
      if (response.ok) {
        setMessage({ type: 'success', text: 'PIN reseteado correctamente' });
        setPinActivo(true);
        sessionStorage.removeItem('pinBypassed');
        setTieneMagicLinkBypass(false);
        setTimeout(() => cambiarVista('principal'), 1500);
      } else {
        setMessage({ type: 'danger', text: data.message || data.error || 'Error al resetear el PIN' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'danger', text: 'Error de conexion' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDesactivarPin = async (e) => {
    e.preventDefault();
    if (!tieneMagicLinkBypass && !validarPin(pinDesactivar)) {
      setMessage({ type: 'danger', text: 'PIN invalido' });
      return;
    }
    try {
      setSubmitting(true);
      setMessage({ type: '', text: '' });
      const token = localStorage.getItem('nfcToken');
      const endpoint = tieneMagicLinkBypass
        ? `/api/usuarios/${userId}/pin/desactivar-bypass`
        : `/api/usuarios/${userId}/pin/desactivar`;
      const body = tieneMagicLinkBypass ? {} : { pin: pinDesactivar };
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (response.ok) {
        setMessage({ type: 'success', text: 'PIN desactivado correctamente' });
        setPinActivo(false);
        if (tieneMagicLinkBypass) {
          sessionStorage.removeItem('pinBypassed');
          setTieneMagicLinkBypass(false);
        }
        setTimeout(() => cambiarVista('principal'), 1500);
      } else {
        setMessage({ type: 'danger', text: data.message || data.error || 'Error al desactivar el PIN' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'danger', text: 'Error de conexion' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetCuenta = async (e) => {
    e.preventDefault();
    if (resetConfirmText !== 'RESET') {
      setMessage({ type: 'danger', text: 'Debes escribir RESET para confirmar' });
      return;
    }
    try {
      setSubmitting(true);
      setMessage({ type: '', text: '' });
      setResetProgress(10);
      const headers = await getAuthHeaders();
      setResetProgress(20);
      const response = await fetch(`/api/usuarios/${userId}/reset-cuenta`, { method: 'POST', headers });
      setResetProgress(80);
      const data = await response.json();
      if (response.ok) {
        setResetProgress(100);
        setResetStats(data.stats);
        setMessage({ type: 'success', text: 'Cuenta reseteada exitosamente. Recarga la pagina para ver los cambios.' });
      } else {
        setResetProgress(0);
        setMessage({ type: 'danger', text: data.error || 'Error al resetear la cuenta' });
      }
    } catch (error) {
      console.error('Error:', error);
      setResetProgress(0);
      setMessage({ type: 'danger', text: 'Error de conexion' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSolicitarReemplazo = async (e) => {
    e.preventDefault();
    if (reemplazoConfirmText !== 'CONFIRMAR') {
      setMessage({ type: 'danger', text: 'Debes escribir CONFIRMAR para continuar' });
      return;
    }
    if (!objetoId) {
      setMessage({ type: 'danger', text: 'No se encontro el ID del objeto' });
      return;
    }
    try {
      setSubmitting(true);
      setMessage({ type: '', text: '' });
      const headers = await getAuthHeaders();
      const response = await fetch(`/api/objetos/${objetoId}/solicitar-reemplazo`, { method: 'POST', headers });
      const data = await response.json();
      if (response.ok) {
        setReemplazoExitoso(true);
        setMessage({ type: 'success', text: 'Tu tarjeta ha sido reemplazada. Recibiras un email de confirmacion.' });
      } else {
        setMessage({ type: 'danger', text: data.error || 'Error al solicitar reemplazo' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'danger', text: 'Error de conexion' });
    } finally {
      setSubmitting(false);
    }
  };

  // ============= PIN INPUT STYLE =============
  const pinInputStyle = {
    fontSize: '20px',
    letterSpacing: '8px',
    textAlign: 'center',
    fontWeight: '600'
  };

  // ============= RENDER SUB-VIEWS =============

  const renderSubViewHeader = (title) => (
    <>
      <button
        onClick={() => cambiarVista('principal')}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          marginBottom: '16px',
          color: COLORS.btnGreen,
          fontWeight: 500,
          fontSize: '14px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        <i className="bi bi-arrow-left"></i> Volver
      </button>
      <h5 style={{ marginBottom: '16px', color: COLORS.textPrimary }}>{title}</h5>
      {message.text && (
        <Alert variant={message.type} className="mb-3">{message.text}</Alert>
      )}
    </>
  );

  const renderContenido = () => {
    if (loading) {
      return (
        <div className="text-center py-4">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
        </div>
      );
    }

    // ============= VISTA PRINCIPAL: COLLAPSIBLE SECTIONS =============
    if (vista === 'principal') {
      return (
        <div>
          {message.text && (
            <Alert variant={message.type} className="mb-3">{message.text}</Alert>
          )}

          {/* 1. Seguridad */}
          <Section icon={<i className="bi bi-shield-lock" />} title="Seguridad" badge={
            pinActivo
              ? <span style={{ backgroundColor: COLORS.badgeGreen, color: '#fff', fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px' }}>Activo</span>
              : <span style={{ backgroundColor: '#E0E0E0', color: '#757575', fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px' }}>Inactivo</span>
          }>
            <div style={{ marginBottom: '12px' }}>
              <strong style={{ fontSize: '14px', color: COLORS.textPrimary }}>PIN de seguridad</strong>
              <div style={{ fontSize: '13px', color: COLORS.textSecondary, marginTop: '4px' }}>
                {pinActivo ? 'Protege tu acceso con un PIN de 3 caracteres' : 'Protege tu cuenta con un PIN'}
              </div>
            </div>

            {tieneMagicLinkBypass && (
              <div style={{
                backgroundColor: COLORS.tipBg,
                border: `1px solid ${COLORS.tipBorder}`,
                borderRadius: '8px',
                padding: '10px 12px',
                marginBottom: '12px',
                fontSize: '13px',
                color: COLORS.headerText,
              }}>
                <strong>Acceso con Magic Link:</strong> Puedes resetear tu PIN sin necesidad de recordar el anterior.
              </div>
            )}

            {!pinActivo ? (
              <button
                onClick={() => cambiarVista('activar')}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: `1.5px solid ${COLORS.btnGreen}`,
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  color: COLORS.btnGreen,
                  fontWeight: 600,
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                Activar PIN
              </button>
            ) : (
              <div style={{ display: 'flex', gap: '8px' }}>
                {tieneMagicLinkBypass ? (
                  <button
                    onClick={() => cambiarVista('reset')}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: `1.5px solid ${COLORS.btnGreen}`,
                      borderRadius: '8px',
                      backgroundColor: '#fff',
                      color: COLORS.btnGreen,
                      fontWeight: 600,
                      fontSize: '13px',
                      cursor: 'pointer',
                    }}
                  >
                    Resetear PIN
                  </button>
                ) : (
                  <button
                    onClick={() => cambiarVista('cambiar')}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: `1.5px solid ${COLORS.btnGreen}`,
                      borderRadius: '8px',
                      backgroundColor: '#fff',
                      color: COLORS.btnGreen,
                      fontWeight: 600,
                      fontSize: '13px',
                      cursor: 'pointer',
                    }}
                  >
                    Cambiar PIN
                  </button>
                )}
                <button
                  onClick={() => cambiarVista('desactivar')}
                  style={{
                    flex: 1,
                    padding: '10px',
                    border: `1.5px solid ${COLORS.cardBorder}`,
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                    color: COLORS.btnRed,
                    fontWeight: 600,
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  Desactivar PIN
                </button>
              </div>
            )}

            <div style={{
              backgroundColor: COLORS.tipBg,
              border: `1px solid ${COLORS.tipBorder}`,
              borderRadius: '8px',
              padding: '10px 12px',
              marginTop: '12px',
              fontSize: '13px',
              color: COLORS.headerText,
            }}>
              Si olvidas tu PIN, puedes solicitar un magic link por correo para acceder sin el.
            </div>
          </Section>

          {/* 2. Gestion de datos - Solo visible si PIN activo */}
          <Section
            icon={<i className="bi bi-folder2-open" />}
            title="Gestion de datos"
            visible={pinActivo && objetos && objetos.length > 0}
          >
            <div style={{ marginBottom: '12px' }}>
              <strong style={{ fontSize: '14px', color: COLORS.textPrimary }}>Borrado selectivo</strong>
              <div style={{ fontSize: '13px', color: COLORS.textSecondary, marginTop: '4px' }}>
                Elimina datos especificos de una mascota u objeto (expediente, cartilla, peso, recordatorios).
              </div>
            </div>
            <button
              onClick={() => setShowResetearModal(true)}
              style={{
                width: '100%',
                padding: '10px',
                border: `1.5px solid ${COLORS.cardBorder}`,
                borderRadius: '8px',
                backgroundColor: '#fff',
                color: COLORS.btnRed,
                fontWeight: 600,
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              Resetear datos de mascota/objeto
            </button>

            {/* Reset Tags Demo */}
            {tieneTagsDemo && (
              <>
                <hr style={{ margin: '16px 0', borderColor: COLORS.cardBorder }} />
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong style={{ fontSize: '14px', color: COLORS.textPrimary }}>Reset Tags Demo</strong>
                    <span style={{ backgroundColor: '#FFF3E0', color: '#E65100', fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px' }}>Demo</span>
                  </div>
                  <div style={{ fontSize: '13px', color: COLORS.textSecondary, marginTop: '4px' }}>
                    Resetea tus tags demo al estado inicial (no afecta tags normales)
                  </div>
                </div>
                <button
                  onClick={() => cambiarVista('resetCuenta')}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: `1.5px solid ${COLORS.cardBorder}`,
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                    color: COLORS.btnRed,
                    fontWeight: 600,
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  Resetear Tags Demo
                </button>
              </>
            )}
          </Section>

          {/* 3. Tarjeta de acceso - Solo visible si hay objetoId */}
          <Section
            icon={<i className="bi bi-credit-card-2-front" />}
            title="Tarjeta de acceso"
            visible={!!objetoId}
          >
            <div style={{ marginBottom: '12px' }}>
              <strong style={{ fontSize: '14px', color: COLORS.textPrimary }}>Perdiste tu tarjeta?</strong>
              <div style={{ fontSize: '13px', color: COLORS.textSecondary, marginTop: '4px' }}>
                Solicita un reemplazo para invalidar la tarjeta anterior permanentemente. Tu PIN se mantendra igual.
              </div>
            </div>
            <button
              onClick={() => cambiarVista('reemplazoTarjeta')}
              style={{
                width: '100%',
                padding: '10px',
                border: `1.5px solid ${COLORS.cardBorder}`,
                borderRadius: '8px',
                backgroundColor: '#fff',
                color: COLORS.btnRed,
                fontWeight: 600,
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              Solicitar Reemplazo de Tarjeta
            </button>
          </Section>

          {/* 4. Google Calendar - Siempre visible */}
          <Section icon={<i className="bi bi-calendar-check" />} title="Google Calendar" badge={
            calendarConnected
              ? <span style={{ backgroundColor: COLORS.badgeGreen, color: '#fff', fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px' }}>Conectado</span>
              : null
          }>
            {calendarConnected ? (
              <>
                <div style={{ marginBottom: '12px' }}>
                  <strong style={{ fontSize: '14px', color: COLORS.textPrimary }}>
                    Conectado{calendarEmail ? ` como ${calendarEmail}` : ''}
                  </strong>
                  <div style={{ fontSize: '13px', color: COLORS.textSecondary, marginTop: '4px' }}>
                    Tus recordatorios se sincronizan automaticamente con Google Calendar.
                  </div>
                </div>
                <button
                  onClick={handleDisconnectCalendar}
                  disabled={calendarLoading}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: `1.5px solid ${COLORS.cardBorder}`,
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                    color: COLORS.textSecondary,
                    fontWeight: 600,
                    fontSize: '14px',
                    cursor: calendarLoading ? 'not-allowed' : 'pointer',
                    opacity: calendarLoading ? 0.6 : 1,
                  }}
                >
                  {calendarLoading ? 'Desconectando...' : 'Desconectar Google Calendar'}
                </button>
              </>
            ) : (
              <>
                <div style={{ marginBottom: '12px' }}>
                  <strong style={{ fontSize: '14px', color: COLORS.textPrimary }}>No conectado</strong>
                  <div style={{ fontSize: '13px', color: COLORS.textSecondary, marginTop: '4px' }}>
                    Sincroniza tus recordatorios automaticamente con Google Calendar.
                  </div>
                </div>
                <button
                  onClick={handleConnectCalendar}
                  disabled={calendarLoading}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: `1.5px solid ${COLORS.btnBlue}`,
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                    color: COLORS.btnBlue,
                    fontWeight: 600,
                    fontSize: '14px',
                    cursor: calendarLoading ? 'not-allowed' : 'pointer',
                    opacity: calendarLoading ? 0.6 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  {calendarLoading ? (
                    'Conectando...'
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                      Conectar Google Calendar
                    </>
                  )}
                </button>
              </>
            )}
          </Section>
        </div>
      );
    }

    // ============= VISTA: REEMPLAZO TARJETA =============
    if (vista === 'reemplazoTarjeta') {
      return (
        <div>
          {renderSubViewHeader('Solicitar Tarjeta de Reemplazo')}
          {reemplazoExitoso ? (
            <div className="mb-3">
              <Alert variant="success">
                <strong>Tarjeta reemplazada exitosamente</strong>
                <p className="mb-0 mt-2">
                  Tu tarjeta anterior ha sido invalidada permanentemente.
                  Contacta a LemmonTags para obtener tu nueva tarjeta fisica.
                </p>
              </Alert>
              <Button variant="primary" className="w-100" onClick={onHide}>Cerrar</Button>
            </div>
          ) : (
            <>
              <Alert variant="danger" className="mb-3">
                <strong>Advertencia:</strong> Esta accion es irreversible.
                <p className="mb-2 mt-2">Esta accion:</p>
                <ul className="mb-0">
                  <li>Invalidara tu tarjeta actual <strong>permanentemente</strong></li>
                  <li>Generara un nuevo codigo de acceso</li>
                  <li>Tu PIN se mantendra igual</li>
                </ul>
                {objetoNombre && <p className="mb-0 mt-2"><strong>Objeto:</strong> {objetoNombre}</p>}
              </Alert>
              <Form onSubmit={handleSolicitarReemplazo}>
                <Form.Group className="mb-3">
                  <Form.Label>Escribe <strong className="text-danger">CONFIRMAR</strong> para continuar:</Form.Label>
                  <Form.Control type="text" value={reemplazoConfirmText} onChange={(e) => setReemplazoConfirmText(e.target.value.toUpperCase())} placeholder="Escribe CONFIRMAR" style={{ fontSize: '16px', letterSpacing: '2px', textAlign: 'center', fontWeight: '600' }} disabled={submitting} />
                </Form.Group>
                <div className="d-grid gap-2">
                  <Button variant="danger" type="submit" disabled={submitting || reemplazoConfirmText !== 'CONFIRMAR'}>
                    {submitting ? <><Spinner size="sm" animation="border" className="me-2" />Procesando...</> : 'Confirmar Reemplazo'}
                  </Button>
                  <Button variant="outline-secondary" onClick={() => cambiarVista('principal')} disabled={submitting}>Cancelar</Button>
                </div>
              </Form>
            </>
          )}
        </div>
      );
    }

    // ============= VISTA: RESET CUENTA (Demo) =============
    if (vista === 'resetCuenta') {
      return (
        <div>
          {renderSubViewHeader('Resetear Tags Demo')}
          {resetProgress > 0 && resetProgress < 100 && <ProgressBar animated now={resetProgress} label={`${resetProgress}%`} className="mb-3" />}
          {resetStats ? (
            <div className="mb-3">
              <Alert variant="success">
                <strong>Reset completado:</strong>
                <ul className="mb-0 mt-2">
                  <li>{resetStats.objetosReseteados} tags demo reseteados</li>
                  <li>{resetStats.tratamientosEliminados} tratamientos eliminados</li>
                  <li>{resetStats.expedientesEliminados} entradas de expediente eliminadas</li>
                  <li>{resetStats.documentosEliminados} documentos eliminados</li>
                  <li>{(resetStats.recordatoriosRootEliminados || 0) + (resetStats.recordatoriosObjetoEliminados || 0)} recordatorios eliminados</li>
                </ul>
              </Alert>
              <Button variant="primary" className="w-100" onClick={() => window.location.reload()}>Recargar Pagina</Button>
            </div>
          ) : (
            <>
              <Alert variant="warning" className="mb-3">
                <strong>Advertencia:</strong> Esta accion es irreversible.
                <p className="mb-2 mt-2">Solo se afectaran tus <strong>tags en modo demo</strong>:</p>
                <ul className="mb-0">
                  <li>Se eliminaran tratamientos de mascotas demo</li>
                  <li>Se eliminaran recordatorios de tags demo</li>
                  <li>Se eliminaran expedientes clinicos de tags demo</li>
                  <li>Se eliminaran documentos de tags demo</li>
                  <li>Los tags demo seran reseteados al estado inicial</li>
                </ul>
                <p className="mb-0 mt-2 text-muted small"><em>Tus tags normales NO seran afectados.</em></p>
              </Alert>
              <Form onSubmit={handleResetCuenta}>
                <Form.Group className="mb-3">
                  <Form.Label>Escribe <strong className="text-danger">RESET</strong> para confirmar:</Form.Label>
                  <Form.Control type="text" value={resetConfirmText} onChange={(e) => setResetConfirmText(e.target.value.toUpperCase())} placeholder="Escribe RESET" style={{ fontSize: '18px', letterSpacing: '4px', textAlign: 'center', fontWeight: '600' }} disabled={submitting} />
                </Form.Group>
                <Button variant="danger" type="submit" className="w-100" disabled={submitting || resetConfirmText !== 'RESET'}>
                  {submitting ? <><Spinner size="sm" animation="border" className="me-2" />Reseteando...</> : 'Confirmar Reset'}
                </Button>
              </Form>
            </>
          )}
        </div>
      );
    }

    // ============= VISTA: ACTIVAR PIN =============
    if (vista === 'activar') {
      return (
        <div>
          {renderSubViewHeader('Activar PIN')}
          <div style={{ backgroundColor: COLORS.tipBg, border: `1px solid ${COLORS.tipBorder}`, borderRadius: '8px', padding: '10px 12px', marginBottom: '16px', fontSize: '13px', color: COLORS.headerText }}>
            El PIN debe tener <strong>3 caracteres alfanumericos</strong> (letras y numeros). Es <strong>case-sensitive</strong>.
          </div>
          <Form onSubmit={handleActivarPin}>
            <Form.Group className="mb-3">
              <Form.Label>Nuevo PIN</Form.Label>
              <Form.Control type="text" maxLength={3} value={nuevoPin} onChange={(e) => setNuevoPin(e.target.value)} placeholder="Ej: a1B" style={pinInputStyle} disabled={submitting} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Confirmar PIN</Form.Label>
              <Form.Control type="text" maxLength={3} value={nuevoPinConfirm} onChange={(e) => setNuevoPinConfirm(e.target.value)} placeholder="Ej: a1B" style={pinInputStyle} disabled={submitting} />
            </Form.Group>
            <Button variant="primary" type="submit" className="w-100" disabled={submitting || nuevoPin.length !== 3 || nuevoPinConfirm.length !== 3}>
              {submitting ? 'Activando...' : 'Activar PIN'}
            </Button>
          </Form>
        </div>
      );
    }

    // ============= VISTA: RESETEAR PIN =============
    if (vista === 'reset') {
      return (
        <div>
          {renderSubViewHeader('Resetear PIN')}
          <div style={{ backgroundColor: COLORS.tipBg, border: `1px solid ${COLORS.tipBorder}`, borderRadius: '8px', padding: '10px 12px', marginBottom: '16px', fontSize: '13px', color: COLORS.headerText }}>
            Como entraste con <strong>magic link</strong>, puedes establecer un nuevo PIN sin necesidad del anterior.
          </div>
          <Form onSubmit={handleResetPin}>
            <Form.Group className="mb-3">
              <Form.Label>Nuevo PIN</Form.Label>
              <Form.Control type="text" maxLength={3} value={pinNuevo} onChange={(e) => setPinNuevo(e.target.value)} placeholder="Ej: a1B" style={pinInputStyle} disabled={submitting} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Confirmar nuevo PIN</Form.Label>
              <Form.Control type="text" maxLength={3} value={pinNuevoConfirm} onChange={(e) => setPinNuevoConfirm(e.target.value)} placeholder="Ej: a1B" style={pinInputStyle} disabled={submitting} />
            </Form.Group>
            <Button variant="warning" type="submit" className="w-100" disabled={submitting || pinNuevo.length !== 3 || pinNuevoConfirm.length !== 3}>
              {submitting ? 'Reseteando...' : 'Resetear PIN'}
            </Button>
          </Form>
        </div>
      );
    }

    // ============= VISTA: CAMBIAR PIN =============
    if (vista === 'cambiar') {
      return (
        <div>
          {renderSubViewHeader('Cambiar PIN')}
          <Form onSubmit={handleCambiarPin}>
            <Form.Group className="mb-3">
              <Form.Label>PIN actual</Form.Label>
              <Form.Control type="text" maxLength={3} value={pinActual} onChange={(e) => setPinActual(e.target.value)} placeholder="***" style={pinInputStyle} disabled={submitting} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Nuevo PIN</Form.Label>
              <Form.Control type="text" maxLength={3} value={pinNuevo} onChange={(e) => setPinNuevo(e.target.value)} placeholder="***" style={pinInputStyle} disabled={submitting} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Confirmar nuevo PIN</Form.Label>
              <Form.Control type="text" maxLength={3} value={pinNuevoConfirm} onChange={(e) => setPinNuevoConfirm(e.target.value)} placeholder="***" style={pinInputStyle} disabled={submitting} />
            </Form.Group>
            <Button variant="primary" type="submit" className="w-100" disabled={submitting || pinActual.length !== 3 || pinNuevo.length !== 3 || pinNuevoConfirm.length !== 3}>
              {submitting ? 'Cambiando...' : 'Cambiar PIN'}
            </Button>
          </Form>
        </div>
      );
    }

    // ============= VISTA: DESACTIVAR PIN =============
    if (vista === 'desactivar') {
      return (
        <div>
          {renderSubViewHeader('Desactivar PIN')}
          <Alert variant="warning">
            <small>
              Al desactivar el PIN, cualquier persona con acceso fisico a tu tag NFC podra acceder a tu panel sin restricciones.
            </small>
          </Alert>
          <Form onSubmit={handleDesactivarPin}>
            {!tieneMagicLinkBypass && (
              <Form.Group className="mb-3">
                <Form.Label>Ingresa tu PIN actual para confirmar</Form.Label>
                <Form.Control type="text" maxLength={3} value={pinDesactivar} onChange={(e) => setPinDesactivar(e.target.value)} placeholder="***" style={pinInputStyle} disabled={submitting} />
              </Form.Group>
            )}
            {tieneMagicLinkBypass && (
              <div style={{ backgroundColor: COLORS.tipBg, border: `1px solid ${COLORS.tipBorder}`, borderRadius: '8px', padding: '10px 12px', marginBottom: '16px', fontSize: '13px', color: COLORS.headerText }}>
                Como entraste con magic link, puedes desactivar el PIN sin necesidad de ingresarlo.
              </div>
            )}
            <Button variant="danger" type="submit" className="w-100" disabled={submitting || (!tieneMagicLinkBypass && pinDesactivar.length !== 3)}>
              {submitting ? 'Desactivando...' : 'Desactivar PIN'}
            </Button>
          </Form>
        </div>
      );
    }
  };

  // ============= MODAL WITH FULLSCREEN ON MOBILE =============
  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .settings-modal .modal-dialog {
            margin: 0 !important;
            max-width: 100vw !important;
            width: 100vw !important;
            height: 100vh !important;
          }
          .settings-modal .modal-content {
            border-radius: 0 !important;
            height: 100vh !important;
            border: none !important;
          }
          .settings-modal .modal-body {
            overflow-y: auto !important;
            -webkit-overflow-scrolling: touch;
          }
        }
      `}</style>
      <Modal show={show} onHide={onHide} centered className="settings-modal">
        <Modal.Header closeButton style={{ borderBottom: `1px solid ${COLORS.cardBorder}` }}>
          <Modal.Title style={{ fontSize: '18px', fontWeight: 600, color: COLORS.textPrimary }}>
            <i className="bi bi-gear me-2" style={{ color: COLORS.headerText }}></i>
            Configuracion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#FAFAFA', padding: '16px' }}>
          {renderContenido()}
        </Modal.Body>
      </Modal>

      <ResetearDatosModal
        show={showResetearModal}
        onHide={() => setShowResetearModal(false)}
        objetos={objetos}
      />
    </>
  );
}
